/**
 * MessageInput Component
 * Input field for sending messages
 */

import React, { useState, useRef } from 'react';
import { Box, TextField, IconButton, Paper, Tooltip, Chip } from '@mui/material';
import { Send as SendIcon, AttachFile as AttachFileIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import agentAPI from '../services/api';

const MessageInput = ({ onSendMessage, disabled, placeholder }) => {
  const [message, setMessage] = useState('');
  // null = idle, 'uploading' = in-flight, string = filename of uploaded template
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleTemplateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setIsUploading(true);
    setUploadedFile(null);
    try {
      await agentAPI.uploadTemplate(file);
      setUploadedFile(file.name);
    } catch (err) {
      // brief error flash, then back to idle
      setUploadedFile('error');
      setTimeout(() => setUploadedFile(null), 3000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      setUploadedFile(null); // clear template badge on send
    }
  };

  const templateActive = uploadedFile && uploadedFile !== 'error';
  const uploadError = uploadedFile === 'error';

  return (
    <Paper elevation={0} sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
      {/* Template badge — shown above input row when a template is active */}
      {(templateActive || uploadError) && (
        <Box sx={{ mb: 1 }}>
          <Chip
            icon={templateActive ? <CheckCircleIcon /> : undefined}
            label={templateActive ? uploadedFile : 'Upload failed'}
            size="small"
            onDelete={templateActive ? () => setUploadedFile(null) : undefined}
            sx={{
              backgroundColor: templateActive ? '#e6f4ea' : '#fdecea',
              color: templateActive ? '#1e6e3a' : '#b71c1c',
              fontWeight: 500,
              fontSize: '0.72rem',
              '& .MuiChip-icon': { color: templateActive ? '#2e7d32' : undefined },
              '& .MuiChip-deleteIcon': { color: '#2e7d32', '&:hover': { color: '#1b5e20' } },
            }}
          />
        </Box>
      )}
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pptx"
          style={{ display: 'none' }}
          onChange={handleTemplateUpload}
        />
        <Tooltip title={isUploading ? 'Uploading...' : 'Upload file'}>
          <span>
            <IconButton
              onClick={() => fileInputRef.current.click()}
              disabled={disabled || isUploading}
              size="small"
              sx={{ color: 'action.active' }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <IconButton
          type="submit"
          color="primary"
          disabled={disabled || !message.trim()}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': { backgroundColor: 'primary.dark' },
            '&:disabled': { backgroundColor: 'action.disabledBackground' },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default MessageInput;